import { useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

interface FileDropzoneProps {
    onDrop: (acceptedFile: File) => void;
    showFileInstructions?: boolean;
    containerClassName?: string;
}

export default function FileDropzone({ onDrop: onFileDrop, showFileInstructions = false, containerClassName = '' }: FileDropzoneProps) {
    const { t } = useTranslation();
    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            if (acceptedFiles.length > 0) {
                onFileDrop(acceptedFiles[0]);
            }
        },
        [onFileDrop]
    );

    const { acceptedFiles, getRootProps, getInputProps, isDragAccept, isDragReject, isFocused } = useDropzone({
        onDrop,
        accept: {
            'text/csv': ['.csv'],
            'application/pdf': ['.pdf'],
        },
        maxFiles: 1,
    });

    const baseClassName =
        'border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 flex flex-col items-center justify-center min-h-64 transition-all duration-200 cursor-pointer hover:border-primary/50 hover:bg-primary/5';
    const focusedClassName = 'border-primary/70 bg-primary/10';
    const acceptClassName = 'border-green-400 bg-green-50 dark:bg-green-900/20';
    const rejectClassName = 'border-red-400 bg-red-50 dark:bg-red-900/20';

    const className = useMemo(() => {
        let classes = [baseClassName];
        if (isFocused) classes.push(focusedClassName);
        if (isDragAccept) classes.push(acceptClassName);
        if (isDragReject) classes.push(rejectClassName);
        return classes.join(' ');
    }, [isFocused, isDragAccept, isDragReject]);

    return (
        <div {...getRootProps()} className={containerClassName}>
            <input {...getInputProps()} />
            <div className={className}>
                {/* Upload Icon */}
                <div className="w-16 h-16 mb-4 text-primary">
                    <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                </div>

                {/* Title */}
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">{t('file_dropdown.upload_bank_statement')}</h3>

                {/* Instructions */}
                <p className="text-center text-gray-600 dark:text-gray-400 mb-6">{t('file_dropdown.drag_and_drop')}</p>

                {/* File Type Icons and Instructions - Only show when showFileInstructions is true */}
                {showFileInstructions && (
                    <>
                        <div className="flex flex-col sm:flex-row gap-4 text-sm text-gray-500 dark:text-gray-400">
                            <div className="flex items-center gap-2">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                    />
                                </svg>
                                <span>{t('file_dropdown.csv_files')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                                    />
                                </svg>
                                <span>{t('file_dropdown.pdf_files')}</span>
                            </div>
                        </div>

                        {/* File Requirements */}
                        <div className="mt-4 text-xs text-gray-500 dark:text-gray-400 space-y-1">
                            <p>{t('file_dropdown.for_csv_ensure')}</p>
                            <p>{t('file_dropdown.for_pdf_well_extract')}</p>
                        </div>
                    </>
                )}

                {/* Selected File Display */}
                {acceptedFiles.length > 0 && (
                    <div className="mt-4 p-3 bg-primary/10 rounded-lg border border-primary/20">
                        <div className="flex items-center gap-2 text-primary">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="font-medium">{acceptedFiles[0].name}</span>
                            <span className="text-xs">({Math.round(acceptedFiles[0].size / 1024)} KB)</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
